import { supabase, isSupabaseReady, getCurrentUser } from '../services/supabase.js';
import { getAuthorizedVideoUrl, generateTTS } from '../services/api.js';
import { get, put, has } from '../services/cache.js';
import { renderNavbar } from '../components/navbar.js';
import { renderPitchCanvas } from '../components/pitch-canvas.js';
import { renderStoryboardReplay } from '../components/storyboard-replay.js';
import { renderTTSPlayer } from '../components/tts-player.js';
import { TTS_ENABLED } from '../utils/constants.js';
import { captureFramesAt } from '../services/video-processor.js';
import { exportDebriefZip } from '../utils/export.js';
import { interpretDeleteResult } from '../utils/delete-result.js';
import { navigate } from '../router.js';
import { escapeHtml, formatDate } from '../utils/html.js';
import { renderAvatarInner } from '../utils/avatar.js';
import { isVideoExpired } from '../utils/video-retention.js';
import { formatKickoff, formatMatchLabel } from '../utils/competitions.js';
import { defaultRevealAt, listQuizPhaseOptions, buildPhaseChallengePayload } from '../utils/quiz.js';
import { buildPlatformShareUrl, buildSocialSharePayload, listSocialPlatforms } from '../utils/social-share.js';
import { generateSocialShareImage } from '../utils/social-image.js';
import { EMOJI_MAP, REACTION_TYPES, summarizeReactions } from '../utils/reactions.js';
import { localizedDebriefVisibilityOptions, debriefVisibilityOption } from '../utils/visibility.js';
import { t, tr } from '../services/i18n.js';

const CONFIDENCE_LABELS = {
  low: () => tr('Confiance faible', 'Low confidence'),
  medium: () => tr('Confiance moyenne', 'Medium confidence'),
  high: () => tr('Confiance élevée', 'High confidence')
};
const CHANGE_TYPE_LABELS = {
  ball_progression: () => tr('Progression du ballon', 'Ball progression'),
  attacking_shape: () => tr('Structure offensive', 'Attacking shape'),
  defensive_line: () => tr('Ligne défensive', 'Defensive line'),
  pressing: 'Pressing',
  space: () => tr('Espaces', 'Spaces'),
  player_movement: () => tr('Mouvement de joueur', 'Player movement'),
  set_piece_setup: () => tr('Mise en place phase arrêtée', 'Set-piece setup'),
  restart_delivery: () => tr('Frappe / remise en jeu', 'Restart delivery')
};

const GAME_PHASE_LABELS = {
  open_play: () => tr('Jeu ouvert', 'Open play'),
  corner: 'Corner',
  free_kick_direct: () => tr('Coup franc direct', 'Direct free kick'),
  free_kick_indirect: () => tr('Coup franc indirect', 'Indirect free kick'),
  penalty: 'Penalty',
  throw_in: () => tr('Touche', 'Throw-in'),
  goal_kick: () => tr('Six mètres', 'Goal kick'),
  kick_off: () => tr('Engagement', 'Kick-off'),
  offside_restart: () => tr('Reprise hors-jeu', 'Offside restart'),
  unknown: () => tr('Phase incertaine', 'Unknown phase')
};

export async function renderDebrief(container, params = {}) {
  const debriefId = params.id;
  if (!debriefId) {
    container.innerHTML = `<div class="page p-16"><p class="text-muted">${tr('ID manquant', 'Missing ID')}</p></div>`;
    return;
  }

  if (!isSupabaseReady()) {
    container.innerHTML = `<div class="page p-16 text-center"><h2 class="heading-2">⚙️ ${t('configRequired')}</h2><p class="text-muted mt-16">${t('addSupabaseEnv')}</p></div>`;
    return;
  }

  container.innerHTML = `
    <div class="page p-16" style="padding-bottom:100px;">
      <div id="debrief-content">
        <div class="glass-card skeleton" style="height:200px;margin-bottom:16px;"></div>
        <div class="glass-card skeleton" style="height:120px;"></div>
      </div>
    </div>
  `;
  renderNavbar(container);

  try {
    let { data, error } = await supabase
      .from('debriefs')
      .select(`
        *,
        competition:competitions (id, name, code),
        match:matches (id, home_team, away_team, kickoff_at, stage),
        user:profiles!debriefs_user_id_fkey (id, pseudo, avatar_url, avatar_emoji)
      `)
      .eq('id', debriefId)
      .single();

    if (error && /competition|match|relationship|column/i.test(error.message || '')) {
      const retry = await supabase
        .from('debriefs')
        .select(`
          *,
          user:profiles!debriefs_user_id_fkey (id, pseudo, avatar_url, avatar_emoji)
        `)
        .eq('id', debriefId)
        .single();
      data = retry.data;
      error = retry.error;
    }

    if (error || !data) throw error || new Error(tr('Debrief introuvable', 'Debrief not found'));

    const raw = data.ai_raw_response || {};
    const currentUser = await getCurrentUser();
    const isOwner = !!currentUser && currentUser.id === data.user_id;

    const analysis = {
      title: data.title,
      summary: data.summary,
      key_moment: data.key_moment,
      tactical_elements: data.tactical_elements || [],
      players_positions: data.players_positions || [],
      ball_position: data.ball_position || {},
      arrows: data.arrows || [],
      tactical_storyboard: data.tactical_storyboard || raw.tactical_storyboard || [],
      // Overlays tactiques optionnels (stockés dans ai_raw_response).
      tactical_lines: raw.tactical_lines || [],
      tactical_shapes: raw.tactical_shapes || [],
      highlight_zones: raw.highlight_zones || [],
      // Signal de fiabilité posé par le Worker quand les positions sont suffisantes.
      tracking_reliable: raw.tracking_reliable === true
    };
    // Passé la rétention (15 min), la vidéo brute a été supprimée de R2 par le
    // cleanup planifié du Worker. On évite alors d'afficher un lecteur cassé :
    // un bloc d'info remplace le <video>, mais toute l'analyse reste affichée.
    const videoExpired = isVideoExpired(data.created_at);
    let videoUrl = '';
    let videoUnavailable = !data.video_key;
    if (!videoExpired && data.video_key) {
      try {
        videoUrl = await getAuthorizedVideoUrl(data.video_key);
      } catch {
        videoUnavailable = true;
      }
    }
    const authorPseudo = data.user?.pseudo || tr('anonyme', 'anonymous');
    const visibility = debriefVisibilityOption(data.visibility);

    const content = container.querySelector('#debrief-content');
    content.innerHTML = `
      <div class="flex-col gap-16">
        <!-- Auteur -->
        <div style="display:flex;align-items:center;gap:12px;">
          <div style="width:40px;height:40px;border-radius:50%;background:linear-gradient(135deg,var(--accent),var(--secondary));display:flex;align-items:center;justify-content:center;font-weight:700;color:#fff;">
            ${renderAvatarInner(data.user)}
          </div>
          <div>
            <p style="font-weight:600;margin:0;">@${escapeHtml(authorPseudo)}</p>
            <p class="text-muted" style="font-size:12px;margin:0;">${escapeHtml(formatDate(data.created_at))} · ${escapeHtml(visibility.badge)}</p>
          </div>
        </div>

        ${data.competition || data.match ? `
          <div class="glass-card" style="padding:12px;">
            <p class="text-muted" style="font-size:12px;margin:0 0 4px;">${tr('Contexte match', 'Match context')}</p>
            <strong>${escapeHtml(data.match ? formatMatchLabel(data.match) : data.competition?.name || tr('Compétition', 'Competition'))}</strong>
            ${data.competition?.name && data.match ? `<p class="text-muted" style="font-size:12px;margin:4px 0 0;">${escapeHtml(data.competition.name)} · ${escapeHtml(formatKickoff(data.match.kickoff_at))}</p>` : ''}
          </div>
        ` : ''}

        <!-- Vidéo (ou info de suppression si rétention dépassée) -->
        ${videoExpired ? `
        <div class="glass-card" style="text-align:center;">
          <p style="font-weight:600;margin:0 0 4px;">${tr('Vidéo brute supprimée', 'Raw video deleted')}</p>
          <p class="text-muted" style="margin:0;font-size:13px;">${tr("La vidéo d'origine a été supprimée automatiquement pour économiser le stockage. L'analyse complète reste disponible ci-dessous.", 'The original video was deleted automatically to save storage. The full analysis remains available below.')}</p>
        </div>` : videoUnavailable ? `
        <div class="glass-card" style="text-align:center;">
          <p style="font-weight:600;margin:0 0 4px;">${tr('Vidéo indisponible', 'Video unavailable')}</p>
          <p class="text-muted" style="margin:0;font-size:13px;">${tr("L'accès temporaire à la vidéo n'a pas pu être validé. L'analyse complète reste disponible ci-dessous.", 'Temporary video access could not be validated. The full analysis remains available below.')}</p>
        </div>` : `
        <div class="video-container">
          <video id="debrief-video" src="${escapeHtml(videoUrl)}" controls playsinline preload="metadata"></video>
        </div>`}

        <div id="storyboard-wrapper"></div>

        <!-- Terrain tactique -->
        <div id="pitch-wrapper"></div>

        <!-- Analyse textuelle -->
        <div class="glass-card">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:12px;flex-wrap:wrap;">
            <h2 class="heading-2" style="margin:0;">${escapeHtml(analysis.title || tr('Analyse', 'Analysis'))}</h2>
            ${renderConfidenceBadge(raw.confidence)}
          </div>
          <p style="line-height:1.6;margin-bottom:16px;">${escapeHtml(analysis.summary || '')}</p>
          ${(analysis.tactical_elements || []).map(el => `
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;padding:10px 12px;background:var(--surface-2);border-radius:8px;">
              <span style="font-size:14px;font-weight:800;color:var(--primary);">${el.type === 'offensive' ? 'OFF' : el.type === 'defensive' ? 'DEF' : el.type === 'set_piece' ? 'CPA' : 'KEY'}</span>
              <span style="font-size:14px;">${escapeHtml(el.description)}</span>
            </div>
          `).join('')}
        </div>

        ${renderObservations(raw)}
        ${renderGamePhase(raw.game_phase)}

        <!-- Key moment -->
        ${analysis.key_moment ? `
          <div class="glass-card" style="border-left:3px solid var(--secondary);">
            <p style="font-weight:600;margin-bottom:4px;">${tr('Moment décisif', 'Decisive moment')}</p>
            <p style="margin:0;font-size:14px;">${escapeHtml(analysis.key_moment)}</p>
          </div>
        ` : ''}

        ${renderFrameTimeline(raw)}

        <!-- Comparaisons d'images clés -->
        <div id="comparisons-wrapper"></div>

        <!-- TTS Player -->
        <div id="tts-wrapper"></div>

        <!-- Réactions -->
        <div class="glass-card">
          <p style="font-weight:600;margin-bottom:12px;">${tr('Réactions', 'Reactions')}</p>
          <div style="display:flex;gap:10px;">
            <button class="reaction-btn" data-type="fire">${tr('Feu', 'Fire')} <small id="count-fire">0</small></button>
            <button class="reaction-btn" data-type="brain">${tr('Analyse', 'Analysis')} <small id="count-brain">0</small></button>
            <button class="reaction-btn" data-type="cap">${tr('Débat', 'Debate')} <small id="count-cap">0</small></button>
          </div>
        </div>

        <!-- Commentaires -->
        <div class="glass-card">
          <p style="font-weight:600;margin-bottom:12px;">${tr('Commentaires', 'Comments')}</p>
          <div id="comments-list" class="flex-col gap-12" style="margin-bottom:12px;"></div>
          <div style="display:flex;gap:8px;">
            <input id="comment-input" class="input-glow" type="text" placeholder="${tr('Ton commentaire...', 'Your comment...')}" style="flex:1;" />
            <button id="btn-comment" class="btn-ghost">${tr('Envoyer', 'Send')}</button>
          </div>
        </div>

        <!-- Actions -->
        <div style="display:flex;gap:8px;flex-wrap:wrap;">
          <button id="btn-share" class="btn-ghost" style="flex:1;min-width:120px;">${tr('Partager', 'Share')}</button>
          <button id="btn-export" class="btn-ghost" style="flex:1;min-width:120px;">${tr('Exporter (ZIP)', 'Export (ZIP)')}</button>
        </div>
        ${isOwner && Array.isArray(raw.frame_previews) && raw.frame_previews.length ? renderQuizCreator(raw) : ''}
        ${isOwner ? `
          <div class="glass-card">
            <p style="font-weight:700;margin:0 0 10px;">${tr('Visibilité', 'Visibility')}</p>
            <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
              <select id="visibility-select" class="input-glow" style="flex:1;min-width:180px;">
                ${localizedDebriefVisibilityOptions().map((option) => `
                  <option value="${escapeHtml(option.value)}" ${option.value === visibility.value ? 'selected' : ''}>${escapeHtml(option.label)} - ${escapeHtml(option.badge)}</option>
                `).join('')}
              </select>
              <button id="btn-save-visibility" class="btn-secondary" type="button">${t('save')}</button>
            </div>
            <p id="visibility-help" class="text-muted" style="font-size:12px;margin:8px 0 0;">${escapeHtml(visibility.description)}</p>
          </div>
        ` : ''}
        <div class="glass-card" id="social-export-card">
          <p style="font-weight:700;margin:0 0 10px;">${tr('Export social', 'Social export')}</p>
          <div class="actions">
            <select id="social-platform" class="input-glow" style="flex:1;min-width:180px;">
              ${listSocialPlatforms().map(p => `<option value="${escapeHtml(p.id)}">${escapeHtml(p.label)}</option>`).join('')}
            </select>
            <button id="btn-social-image" class="btn-secondary" type="button">Image</button>
            <button id="btn-social-copy" class="btn-secondary" type="button">${tr('Texte', 'Text')}</button>
            <button id="btn-social-open" class="btn-secondary" type="button">${tr('Poster', 'Post')}</button>
            <a class="btn-secondary" href="#/share/${escapeHtml(data.id)}">${tr('Preview', 'Preview')}</a>
          </div>
          <div id="social-preview" style="margin-top:12px;"></div>
        </div>
        ${isOwner ? `<button id="btn-delete" class="btn-ghost" style="width:100%;color:var(--danger);border-color:var(--danger);">${tr('Supprimer cette analyse', 'Delete this analysis')}</button>` : ''}
      </div>
    `;

    renderStoryboardReplay(content.querySelector('#storyboard-wrapper'), analysis.tactical_storyboard, content.querySelector('#debrief-video'), analysis);

    // Pitch Canvas
    const pitchWrapper = content.querySelector('#pitch-wrapper');
    if (analysis.players_positions || analysis.ball_position) {
      renderPitchCanvas(pitchWrapper, analysis);
    }

    // TTS — désactivé tant qu'aucun provider voix n'est fiable (VITE_TTS_ENABLED).
    // On ne montre aucun lecteur (pas de commande inutilisable) et on n'appelle
    // jamais /api/tts. Réactivable via le flag, sans toucher au code.
    if (TTS_ENABLED) {
      const ttsWrapper = content.querySelector('#tts-wrapper');
      const textToRead = [analysis.title, analysis.summary, analysis.key_moment].filter(Boolean).join('. ');
      if (textToRead) {
        renderTTSPlayer(ttsWrapper, textToRead);
      }
    }

    // Reactions
    loadReactions(debriefId, content, currentUser);
    content.querySelectorAll('.reaction-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const type = btn.dataset.type;
        if (!currentUser) {
          showToast(tr('Connecte-toi pour réagir.', 'Sign in to react.'));
          navigate('#/login');
          return;
        }
        btn.disabled = true;
        try {
          const result = await toggleReaction(debriefId, type, currentUser);
          showToast(result.active ? tr('Réaction ajoutée.', 'Reaction added.') : tr('Réaction retirée.', 'Reaction removed.'));
          await loadReactions(debriefId, content, currentUser);
        } catch (err) {
          showToast(tr('Réaction impossible: ', 'Reaction failed: ') + err.message);
          await loadReactions(debriefId, content, currentUser);
        } finally {
          btn.disabled = false;
        }
      });
    });

    // Comments
    loadComments(debriefId, content);
    const commentInput = content.querySelector('#comment-input');
    content.querySelector('#btn-comment').addEventListener('click', async () => {
      const text = commentInput.value.trim();
      if (!text) return;
      await addComment(debriefId, text);
      commentInput.value = '';
      loadComments(debriefId, content);
    });

    // Share
    content.querySelector('#btn-share').addEventListener('click', async () => {
      const shareData = {
        title: analysis.title || 'fooview',
        text: analysis.summary || tr('Analyse tactique sur fooview', 'Tactical analysis on fooview'),
        url: window.location.href
      };
      if (navigator.share) {
        try { await navigator.share(shareData); } catch {}
      } else {
        await navigator.clipboard.writeText(window.location.href);
        showToast(tr('Lien copié !', 'Link copied!'));
      }
    });

    const socialSelect = content.querySelector('#social-platform');
    const socialPreview = content.querySelector('#social-preview');
    content.querySelector('#btn-social-copy').addEventListener('click', async () => {
      const payload = buildSocialSharePayload(data, { platform: socialSelect.value, url: window.location.href });
      await navigator.clipboard.writeText(payload.text);
      socialPreview.innerHTML = renderSocialTextPreview(payload);
      showToast(tr('Texte social copié.', 'Social text copied.'));
    });
    content.querySelector('#btn-social-open').addEventListener('click', () => {
      const payload = buildSocialSharePayload(data, { platform: socialSelect.value, url: window.location.href });
      const shareUrl = buildPlatformShareUrl(payload.platform.id, { text: payload.text, url: window.location.href });
      socialPreview.innerHTML = renderSocialTextPreview(payload);
      if (shareUrl) window.open(shareUrl, '_blank', 'noopener,noreferrer');
      else showToast(tr('Plateforme sans intent web : copie le texte et télécharge l’image.', 'Platform has no web intent: copy the text and download the image.'));
    });
    content.querySelector('#btn-social-image').addEventListener('click', async (event) => {
      const btn = event.currentTarget;
      btn.disabled = true;
      btn.textContent = tr('Génération...', 'Generating...');
      try {
        const payload = buildSocialSharePayload(data, { platform: socialSelect.value, url: window.location.href });
        const imageUrl = await generateSocialShareImage(data, { platform: socialSelect.value, url: window.location.href });
        socialPreview.innerHTML = `
          ${renderSocialTextPreview(payload)}
          <img src="${imageUrl}" alt="${tr('Aperçu social', 'Social preview')}" style="width:100%;max-height:520px;object-fit:contain;border:1px solid var(--line);border-radius:8px;margin-top:10px;background:var(--surface-2);" />
          <button id="btn-download-social" class="btn-primary" type="button" style="width:100%;margin-top:10px;">${tr('Télécharger le PNG', 'Download PNG')}</button>
        `;
        socialPreview.querySelector('#btn-download-social').addEventListener('click', () => downloadDataUrl(imageUrl, `fooview-${payload.platform.id}.png`));
      } catch (err) {
        showToast(tr('Image sociale impossible: ', 'Social image failed: ') + err.message);
      } finally {
        btn.disabled = false;
        btn.textContent = tr('Image', 'Image');
      }
    });

    // Export ZIP (markdown lisible + metadata JSON)
    content.querySelector('#btn-export').addEventListener('click', () => {
      try {
        exportDebriefZip(data);
        showToast(tr('Export ZIP généré.', 'ZIP export generated.'));
      } catch (err) {
        showToast(tr('Export impossible: ', 'Export failed: ') + err.message);
      }
    });

    const quizBtn = content.querySelector('#btn-create-quiz');
    if (quizBtn) {
      quizBtn.addEventListener('click', async () => {
        quizBtn.disabled = true;
        const phaseSelect = content.querySelector('#quiz-phase');
        const phaseId = phaseSelect ? phaseSelect.value : 'full';
        const payload = buildPhaseChallengePayload(raw, phaseId);
        const { error: quizError } = await supabase.from('match_challenges').insert({
          creator_id: currentUser.id,
          competition_id: data.competition_id || null,
          match_id: data.match_id || null,
          source_debrief_id: data.id,
          title: payload.title || analysis.title || tr('Quiz tactique', 'Tactical quiz'),
          prompt: payload.prompt,
          frame_previews: payload.frame_previews,
          ai_answer: payload.ai_answer,
          reveal_at: defaultRevealAt()
        });
        if (quizError) {
          quizBtn.disabled = false;
          showToast(tr('Quiz indisponible: applique la migration Supabase.', 'Quiz unavailable: apply the Supabase migration.'));
          return;
        }
        showToast(tr('Quiz créé.', 'Quiz created.'));
        setTimeout(() => { window.location.hash = '#/quiz'; }, 500);
      });
    }

    const visibilitySelect = content.querySelector('#visibility-select');
    const visibilityHelp = content.querySelector('#visibility-help');
    if (visibilitySelect && visibilityHelp) {
      visibilitySelect.addEventListener('change', () => {
        visibilityHelp.textContent = debriefVisibilityOption(visibilitySelect.value).description;
      });
    }
    const btnSaveVisibility = content.querySelector('#btn-save-visibility');
    if (btnSaveVisibility) {
      btnSaveVisibility.addEventListener('click', async () => {
        const nextVisibility = debriefVisibilityOption(visibilitySelect.value).value;
        btnSaveVisibility.disabled = true;
        btnSaveVisibility.textContent = tr('Sauvegarde...', 'Saving...');
        const { data: updated, error: visibilityError } = await supabase
          .from('debriefs')
          .update({ visibility: nextVisibility })
          .eq('id', debriefId)
          .eq('user_id', currentUser.id)
          .select('id, visibility')
          .single();

        if (visibilityError || !updated) {
          showToast(tr('Visibilite impossible: migration Supabase a verifier.', 'Visibility update failed: check the Supabase migration.'));
          btnSaveVisibility.disabled = false;
          btnSaveVisibility.textContent = t('save');
          return;
        }
        data.visibility = updated.visibility;
        const saved = debriefVisibilityOption(updated.visibility);
        visibilityHelp.textContent = saved.description;
        showToast(tr(`Visibilite mise a jour: ${saved.badge}.`, `Visibility updated: ${saved.badge}.`));
        btnSaveVisibility.disabled = false;
        btnSaveVisibility.textContent = t('save');
      });
    }

    // Suppression (propriétaire uniquement, garde-fou côté requête + RLS)
    const btnDelete = content.querySelector('#btn-delete');
    if (btnDelete) {
      btnDelete.addEventListener('click', async () => {
        if (!confirm(tr('Supprimer définitivement cette analyse ? Cette action est irréversible.', 'Permanently delete this analysis? This action cannot be undone.'))) return;
        btnDelete.disabled = true;
        btnDelete.textContent = tr('Suppression...', 'Deleting...');
        // On demande à Supabase de renvoyer les lignes réellement supprimées :
        // sans ça, une suppression bloquée par RLS réussit sans rien supprimer.
        const { data: deleted, error: delError } = await supabase
          .from('debriefs')
          .delete()
          .eq('id', debriefId)
          .eq('user_id', currentUser.id)
          .select('id');
        const result = interpretDeleteResult({ error: delError, data: deleted });
        if (!result.ok) {
          showToast(result.message);
          btnDelete.disabled = false;
          btnDelete.textContent = tr('Supprimer cette analyse', 'Delete this analysis');
          return;
        }
        showToast(result.message);
        navigate('#/home');
      });
    }

    // Comparaisons d'images clés (frames reconstruites depuis la vidéo)
    renderComparisons(content.querySelector('#comparisons-wrapper'), videoUrl, raw, videoExpired || videoUnavailable);

  } catch (e) {
    container.querySelector('#debrief-content').innerHTML = `<p class="text-muted">${tr('Erreur', 'Error')}: ${escapeHtml(e.message)}</p>`;
  }
}

function renderSocialTextPreview(payload) {
  return `
    <div style="padding:10px;border:1px solid var(--line);border-radius:8px;background:var(--surface-2);">
      <p class="text-muted" style="font-size:12px;margin:0 0 6px;">${escapeHtml(payload.platform.label)} · ${escapeHtml(payload.format.label)} ${payload.format.width}x${payload.format.height}</p>
      <p style="white-space:pre-wrap;margin:0;font-size:13px;line-height:1.5;">${escapeHtml(payload.text)}</p>
    </div>
  `;
}

// Bloc "Créer un quiz" pour le propriétaire. Si l'analyse contient des
// comparaisons d'images exploitables, on propose de choisir la phase à publier
// (sinon le bouton seul suffit : "Analyse complète" reste l'option par défaut).
function renderQuizCreator(raw) {
  const options = listQuizPhaseOptions(raw);
  const hasPhases = options.length > 1;
  return `
    <div class="glass-card" id="quiz-creator">
      <p style="font-weight:700;margin:0 0 10px;">${tr('Créer un quiz', 'Create a quiz')}</p>
      ${hasPhases ? `
        <label for="quiz-phase" class="text-muted" style="font-size:12px;display:block;margin:0 0 6px;">${tr("Phase analysée par l'IA à utiliser", 'AI-analyzed phase to use')}</label>
        <select id="quiz-phase" class="input-glow" style="width:100%;margin-bottom:12px;">
          ${options.map(o => `<option value="${escapeHtml(o.id)}">${escapeHtml(o.label)}${o.detail ? ` — ${escapeHtml(o.detail)}` : ''}</option>`).join('')}
        </select>` : ''}
      <button id="btn-create-quiz" class="btn-ghost" style="width:100%;">${tr('Créer un quiz', 'Create a quiz')}</button>
    </div>`;
}

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const OBSERVATION_LABELS = {
  ball_location: () => tr('Ballon', 'Ball'),
  direction_of_play: () => tr('Sens du jeu', 'Direction of play'),
  team_dark_kit: () => tr('Maillot foncé', 'Dark kit'),
  team_light_kit: () => tr('Maillot clair', 'Light kit'),
  goal_orientation: () => tr('Orientation buts', 'Goal orientation'),
  possession: 'Possession',
  body_orientation: () => tr('Orientation joueurs', 'Player body orientation'),
  pressing_or_retreat: () => tr('Pressing / repli', 'Pressing / retreat')
};

function renderConfidenceBadge(confidence) {
  const label = typeof CONFIDENCE_LABELS[confidence] === 'function' ? CONFIDENCE_LABELS[confidence]() : CONFIDENCE_LABELS[confidence];
  if (!label) return '';
  const color = confidence === 'high' ? 'var(--success)' : confidence === 'medium' ? 'var(--primary)' : 'var(--muted)';
  return `<span style="font-size:12px;font-weight:800;padding:4px 10px;border-radius:999px;border:1px solid ${color};color:${color};white-space:nowrap;">${escapeHtml(label)}</span>`;
}

function renderObservations(raw) {
  const obs = raw && typeof raw.observations === 'object' ? raw.observations : null;
  if (!obs) return '';
  const rows = Object.entries(OBSERVATION_LABELS)
    .filter(([key]) => obs[key] && String(obs[key]).trim())
    .map(([key, label]) => `
      <div style="display:flex;gap:8px;padding:8px 0;border-bottom:1px solid var(--line);">
        <span class="text-muted" style="font-size:13px;min-width:130px;font-weight:600;">${typeof label === 'function' ? label() : label}</span>
        <span style="font-size:13px;">${escapeHtml(String(obs[key]))}</span>
      </div>`);
  if (!rows.length && !obs._caveat) return '';
  return `
    <div class="glass-card">
      <p style="font-weight:600;margin-bottom:8px;">${tr('Observations factuelles', 'Factual observations')}</p>
      ${obs._caveat ? `<p class="text-muted" style="font-size:13px;margin:0 0 10px;padding:8px 10px;background:var(--surface-2);border-radius:8px;">${escapeHtml(obs._caveat)}</p>` : ''}
      ${rows.join('')}
    </div>`;
}

function renderGamePhase(gamePhase) {
  if (!gamePhase || typeof gamePhase !== 'object') return '';
  const label = GAME_PHASE_LABELS[gamePhase.type];
  const phaseLabel = gamePhase.label || (typeof label === 'function' ? label() : label) || gamePhase.type;
  const confidence = typeof CONFIDENCE_LABELS[gamePhase.confidence] === 'function'
    ? CONFIDENCE_LABELS[gamePhase.confidence]()
    : CONFIDENCE_LABELS[gamePhase.confidence] || gamePhase.confidence || '';
  const evidence = String(gamePhase.evidence || '').trim();
  const focus = String(gamePhase.analysis_focus || '').trim();
  if (!phaseLabel && !evidence && !focus) return '';
  return `
    <div class="glass-card">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:8px;flex-wrap:wrap;">
        <p style="font-weight:600;margin:0;">${tr('Phase de jeu', 'Game phase')}</p>
        ${confidence ? `<span class="comparison-tag">${escapeHtml(confidence)}</span>` : ''}
      </div>
      <p style="margin:0 0 8px;font-size:15px;font-weight:800;">${escapeHtml(phaseLabel)}</p>
      ${evidence ? `<p class="text-muted" style="font-size:13px;margin:0 0 8px;">${escapeHtml(evidence)}</p>` : ''}
      ${focus ? `<p style="font-size:13px;margin:0;line-height:1.5;">${escapeHtml(focus)}</p>` : ''}
    </div>`;
}

function renderFrameTimeline(raw) {
  const previews = Array.isArray(raw?.frame_previews) ? raw.frame_previews.filter(Boolean) : [];
  if (!previews.length) return '';
  return `
    <div class="glass-card frame-timeline-card">
      <div style="display:flex;align-items:baseline;justify-content:space-between;gap:10px;margin-bottom:12px;flex-wrap:wrap;">
        <div>
          <p style="font-weight:700;margin:0;">${tr("Décomposition complète de l'action", 'Full action breakdown')}</p>
          <p class="text-muted" style="font-size:12px;margin:3px 0 0;">${tr("Images réellement envoyées à l'analyse, dans l'ordre chronologique.", 'Frames actually sent to analysis, in chronological order.')}</p>
        </div>
        <span class="comparison-tag">${previews.length} ${tr('images', 'frames')}</span>
      </div>
      <div class="frame-timeline">
        ${previews.map((src, index) => `
          <figure class="timeline-frame">
            <img src="${escapeHtml(src)}" alt="" loading="lazy" />
            <figcaption>${tr('Image', 'Frame')} ${index + 1}${Array.isArray(raw.frame_times) && Number.isFinite(raw.frame_times[index]) ? ` · ${Number(raw.frame_times[index]).toFixed(1)}s` : ''}</figcaption>
          </figure>
        `).join('')}
      </div>
    </div>
  `;
}

// Mappe un index de frame vers la seconde correspondante dans la fenêtre analysée.
// Analyses récentes : on lit l'instant EXACT réellement capturé (raw.frame_times) ;
// c'est désormais le cas général, l'extraction (computeFrameSampleTimes) stockant les
// timestamps exacts, borne de fin incluse.
// Anciennes analyses (SANS frame_times) UNIQUEMENT : repli sur l'ANCIENNE formule
// d'extraction (t = start + step*i + 0.1, borné à end-0.01, step = span / frame_count).
// Cette formule s'arrêtait avant `end` (un but en fin d'action pouvait être manqué) et
// n'est conservée que pour rester cohérent avec la recapture de ces analyses héritées.
function frameToSeconds(idx, raw) {
  const i = Number.isFinite(idx) ? idx : 0;
  if (Array.isArray(raw.frame_times) && Number.isFinite(raw.frame_times[i])) {
    return raw.frame_times[i];
  }
  const fc = Number(raw.frame_count) > 0 ? Number(raw.frame_count) : 8;
  const seg = raw.segment && Number.isFinite(raw.segment.start) ? raw.segment : null;
  const start = seg ? seg.start : 0;
  const end = seg ? seg.end : 15;
  const span = Math.max(0.1, end - start);
  const step = span / fc;
  return Math.min(end - 0.01, start + step * i + 0.1);
}

// Libellés pros mais lisibles pour l'analyse par image (plateau pédagogique).
const FRAME_FIELD_LABELS = {
  players: () => tr('Position des joueurs', 'Player positions'),
  shape: () => tr('Schéma', 'Shape'),
  imbalance: () => tr('Déséquilibre', 'Imbalance'),
  pressure: 'Impact'
};

// Une ligne "clé : valeur" de l'analyse d'une image (uniquement si renseignée).
function frameRow(key, value) {
  if (!value) return '';
  const fieldLabel = FRAME_FIELD_LABELS[key];
  return `<div class="frame-row"><span class="frame-key">${escapeHtml(typeof fieldLabel === 'function' ? fieldLabel() : fieldLabel)}</span><span class="frame-val">${escapeHtml(value)}</span></div>`;
}

// Colonne image + panneau d'analyse propre à cette image. Si l'analyse par image
// est absente (anciennes analyses), on n'affiche aucun faux détail : seule la
// synthèse globale, plus bas, décrit le changement.
function renderFrameColumn(slot, i, label, analysis, time) {
  const rows = analysis
    ? frameRow('players', analysis.players) +
      frameRow('shape', analysis.shape) +
      frameRow('imbalance', analysis.imbalance) +
      frameRow('pressure', analysis.pressure)
    : '';
  const panel = rows
    ? `<div class="frame-analysis"><span class="frame-label">${escapeHtml(label)}</span>${rows}</div>`
    : '';
  // Timestamp discret : repère utile pour vérifier l'écart temporel réel des images.
  const timeLabel = Number.isFinite(time)
    ? `<span class="text-muted" style="display:block;font-size:11px;margin-top:4px;text-align:center;">${escapeHtml(label)} · ${time.toFixed(1)}s</span>`
    : '';
  return `
    <div class="comparison-col">
      <div class="comparison-img" data-slot="${slot}-${i}"><span class="text-muted" style="font-size:12px;">…</span></div>
      ${timeLabel}
      ${panel}
    </div>`;
}

// Analyse dense « magazine » : on rend chaque paragraphe séparément pour une
// lecture aérée. Vide → rien (les anciennes analyses gardent la synthèse courte).
function renderBreakdown(text) {
  if (!text || !String(text).trim()) return '';
  const paras = String(text).split(/\n{2,}|\n/).map(p => p.trim()).filter(Boolean);
  if (!paras.length) return '';
  return `<div class="cmp-breakdown">${paras
    .map(p => `<p style="margin:0 0 8px;font-size:14px;line-height:1.6;">${escapeHtml(p)}</p>`)
    .join('')}</div>`;
}

// Point tactique ciblé (construction offensive, bloc défensif, déstabilisation,
// enseignement). Affiché seulement s'il est renseigné.
function renderTacticalDetail(label, value) {
  if (!value || !String(value).trim()) return '';
  return `<div class="cmp-detail"><span class="cmp-detail-label">${escapeHtml(label)}</span><span class="cmp-detail-val">${escapeHtml(String(value).trim())}</span></div>`;
}

function renderComparisons(wrapper, videoUrl, raw, videoExpired = false) {
  if (!wrapper) return;
  const comparisons = Array.isArray(raw.key_frame_comparisons) ? raw.key_frame_comparisons : [];

  if (!comparisons.length) {
    // Fallback honnête : pas de frames inventées.
    wrapper.innerHTML = `
      <div class="glass-card">
        <p style="font-weight:600;margin-bottom:8px;">${tr("Comparaisons d'images clés", 'Key frame comparisons')}</p>
        <p class="text-muted" style="margin:0;">${tr("Aucun moment de comparaison structuré pour cette analyse. La détection automatique des changements de position alimente cette section pour les analyses récentes ; l'extraction côté serveur reste à enrichir.", 'No structured comparison moment for this analysis. Automatic position-change detection feeds this section for recent analyses; server extraction still needs enrichment.')}</p>
      </div>`;
    return;
  }

  // Aperçus persistés des frames réellement envoyées à l'IA (analyses récentes).
  const previews = Array.isArray(raw.frame_previews) ? raw.frame_previews : null;

  wrapper.innerHTML = `
    <div class="glass-card">
      <p style="font-weight:600;margin-bottom:12px;">${tr("Comparaisons d'images clés", 'Key frame comparisons')}</p>
      <div id="cmp-list" class="flex-col gap-20"></div>
    </div>`;
  const list = wrapper.querySelector('#cmp-list');

  comparisons.forEach((c, i) => {
    const block = document.createElement('div');
    block.className = 'comparison-block';
    block.innerHTML = `
      <div class="comparison-pair">
        ${renderFrameColumn('a', i, tr('Avant', 'Before'), c.from_analysis, frameToSeconds(c.from_frame, raw))}
        ${renderFrameColumn('b', i, tr('Après', 'After'), c.to_analysis, frameToSeconds(c.to_frame, raw))}
      </div>
      <div class="comparison-text">
        <span class="comparison-tag">${escapeHtml(typeof CHANGE_TYPE_LABELS[c.change_type] === 'function' ? CHANGE_TYPE_LABELS[c.change_type]() : CHANGE_TYPE_LABELS[c.change_type] || c.change_type || tr('Changement', 'Change'))}</span>
        ${c.description ? `<p style="margin:8px 0 0;font-size:14px;line-height:1.5;"><strong>${tr("Ce que ça change", 'What changes')}:</strong> ${escapeHtml(c.description)}</p>` : ''}
        ${renderBreakdown(c.tactical_breakdown)}
        ${renderTacticalDetail(tr('Construction offensive', 'Attacking structure'), c.attacking_structure)}
        ${renderTacticalDetail(tr('Bloc défensif', 'Defensive block'), c.defensive_structure)}
        ${renderTacticalDetail(tr('Déstabilisation', 'Destabilization'), c.destabilization)}
        ${renderTacticalDetail(tr('À retenir', 'Takeaway'), c.coaching_point)}
        ${c.observations ? `<p class="text-muted" style="margin:8px 0 0;font-size:12px;">${tr('Indices visibles', 'Visible clues')}: ${escapeHtml(c.observations)}</p>` : ''}
      </div>`;
    list.appendChild(block);
  });

  const placeholder = `<span class="text-muted" style="font-size:12px;">${tr('Image indisponible', 'Image unavailable')}</span>`;

  // Analyses récentes : on affiche directement les aperçus persistés, qui sont
  // les frames RÉELLEMENT analysées. Plus de recapture vidéo (qui pouvait
  // retomber sur le même keyframe et produire des Avant/Après identiques).
  if (previews) {
    comparisons.forEach((c, i) => {
      const slotA = list.querySelector(`[data-slot="a-${i}"]`);
      const slotB = list.querySelector(`[data-slot="b-${i}"]`);
      const imgA = previews[c.from_frame];
      const imgB = previews[c.to_frame];
      slotA.innerHTML = imgA ? `<img src="${imgA}" alt="" />` : placeholder;
      slotB.innerHTML = imgB ? `<img src="${imgB}" alt="" />` : placeholder;
    });
    return;
  }

  // Rétrocompat (anciennes analyses sans frame_previews) : recapture depuis la
  // vidéo. Si la vidéo brute a été supprimée (rétention dépassée), inutile de
  // tenter une recapture qui échouerait : on affiche directement le placeholder.
  if (videoExpired) {
    list.querySelectorAll('.comparison-img').forEach(el => {
      el.innerHTML = placeholder;
    });
    return;
  }

  const times = [];
  comparisons.forEach(c => {
    times.push(frameToSeconds(c.from_frame, raw));
    times.push(frameToSeconds(c.to_frame, raw));
  });

  captureFramesAt(videoUrl, times).then(imgs => {
    comparisons.forEach((c, i) => {
      const slotA = list.querySelector(`[data-slot="a-${i}"]`);
      const slotB = list.querySelector(`[data-slot="b-${i}"]`);
      if (imgs[i * 2]) slotA.innerHTML = `<img src="${imgs[i * 2]}" alt="" />`;
      if (imgs[i * 2 + 1]) slotB.innerHTML = `<img src="${imgs[i * 2 + 1]}" alt="" />`;
    });
  }).catch(() => {
    // L'analyse textuelle structurée reste affichée même sans images.
    list.querySelectorAll('.comparison-img').forEach(el => {
      el.innerHTML = placeholder;
    });
  });
}

async function loadReactions(debriefId, container, currentUser = null) {
  const { data, error } = await supabase
    .from('reactions')
    .select('emoji,user_id')
    .eq('debrief_id', debriefId)
    .order('created_at', { ascending: true });

  if (error) {
    showToast(tr('Réactions indisponibles: ', 'Reactions unavailable: ') + error.message);
    return;
  }

  const { counts, activeType } = summarizeReactions(data || [], currentUser?.id || null);
  REACTION_TYPES.forEach((type) => {
    const count = container.querySelector(`#count-${type}`);
    const btn = container.querySelector(`.reaction-btn[data-type="${type}"]`);
    if (count) count.textContent = counts[type];
    if (btn) btn.classList.toggle('active', activeType === type);
  });
}

async function toggleReaction(debriefId, type, user) {
  const emoji = EMOJI_MAP[type];
  if (!emoji) throw new Error(tr('type inconnu', 'unknown type'));
  if (!user?.id) throw new Error(tr('session requise', 'session required'));

  const { data: existingRows, error: readError } = await supabase
    .from('reactions')
    .select('id,emoji')
    .eq('debrief_id', debriefId)
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });
  if (readError) throw readError;

  const existing = Array.isArray(existingRows) && existingRows.length ? existingRows[0] : null;
  if (existing) {
    const { error: deleteError } = await supabase
      .from('reactions')
      .delete()
      .eq('debrief_id', debriefId)
      .eq('user_id', user.id);
    if (deleteError) throw deleteError;
    if (existing.emoji === emoji) return { active: false };
  }

  const { error: insertError } = await supabase
    .from('reactions')
    .insert({ debrief_id: debriefId, user_id: user.id, emoji });
  if (insertError) throw insertError;
  return { active: true };
}

async function loadComments(debriefId, container) {
  const { data } = await supabase
    .from('comments')
    .select('*, user:profiles!comments_user_id_fkey (pseudo, avatar_url, avatar_emoji)')
    .eq('debrief_id', debriefId)
    .order('created_at', { ascending: true });

  const list = container.querySelector('#comments-list');
  if (!data || data.length === 0) {
    list.innerHTML = `<p class="text-muted" style="font-size:14px;">${tr('Aucun commentaire', 'No comments')}</p>`;
    return;
  }

  list.innerHTML = data.map(c => `
    <div style="display:flex;gap:10px;">
      <div style="width:32px;height:32px;border-radius:50%;background:linear-gradient(135deg,var(--accent),var(--secondary));display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px;color:#fff;flex-shrink:0;">
        ${renderAvatarInner(c.user)}
      </div>
      <div>
        <p style="font-weight:600;font-size:13px;margin:0;">@${escapeHtml(c.user?.pseudo || tr('anonyme', 'anonymous'))}</p>
        <p style="font-size:14px;margin:4px 0 0;line-height:1.4;">${escapeHtml(c.content)}</p>
        <p class="text-muted" style="font-size:11px;margin:4px 0 0;">${escapeHtml(formatDate(c.created_at))}</p>
      </div>
    </div>
  `).join('');
}

async function addComment(debriefId, content) {
  const { data: { user } } = await supabase.auth.getUser();
  await supabase.from('comments').insert({ debrief_id: debriefId, user_id: user.id, content });
}

function showToast(msg) {
  const t = document.createElement('div');
  t.className = 'toast show';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => { t.className = 'toast'; setTimeout(() => t.remove(), 300); }, 3000);
}
